import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { FormErrors, WrappedFieldArrayProps } from 'redux-form';
import { FieldArray } from 'redux-form';

import type FieldWorkClassificationCodeListItemVM from 'ab-viewModels/fieldWorkClassificationCode/listItem';
import type OccupiedSlotsForWorkOrderVM from 'ab-viewModels/timeSheet/occupiedSlotsForWorkOrder.viewModel';
import type { EquipmentListItemVM } from 'ab-viewModels/timeSplitEquipment/timeSplitEquipment';
import type { SubjobVM } from 'ab-viewModels/workRequest/jobUpsert.viewModel';

import { getToLookupByKey } from '@acceligentllc/shared/utils/array';
import { isAllowed } from '@acceligentllc/shared/utils/auth';
import * as TimeUtils from '@acceligentllc/shared/utils/time';
import * as TimeSheetUtils from '@acceligentllc/shared/utils/timeSheet';
import { findOverlapsByVirtualId, getTimelineEntities } from '@acceligentllc/shared/utils/timeSheetEntry';

import { TimeSheetApprovalStatus } from '@acceligentllc/shared/enums/timeSheetApprovalStatus';
import { TimeSheetEntryWorkType } from '@acceligentllc/shared/enums/timeSheetEntryWorkType';
import { TimeSheetSignatureStatus } from '@acceligentllc/shared/enums/timeSheetSignatureStatus';
import type { WorkOrderReviewStatus } from '@acceligentllc/shared/enums/workOrderReviewStatus';

import { DEFAULT_TIME_DURATION_MINUTE_ROUNDING_INTERVAL } from '@acceligentllc/shared/constants/value';

import type { RootState } from 'af-reducers';

import { bemElement } from 'af-utils/bem.util';

import PagePermissions from 'ab-enums/pagePermissions.enum';

import type { EntryValidationErrors } from '.';
import type { TimeSheetAddedEntryWithType, TimeSheetEntryFormModel, TimeSheetEntryWithType, TimeSheetGapEntryWithType, TimeSheetOccupiedEntryWithType } from './formModel';
import TimeSheetEditFormModel, { TimeAllocationEntryFormModel, TimeSheetEntryFormType, TimeSplitEntryFormModel } from './formModel';
import PerDiemForTimeSheet from './PerDiemForTimeSheet';
import RejectionBanner from './RejectionBanner';
import styles from './styles.module.scss';
import type { OwnProps as TimeAllocationEntriesListProps } from './TimeAllocations/TimeAllocationEntriesList';
import TimeAllocationEntriesList from './TimeAllocations/TimeAllocationEntriesList';
import type { OwnProps as TimeSheetEntriesListProps } from './TimeSheetEntries/TimeSheetEntriesList';
import TimeSheetEntriesList from './TimeSheetEntries/TimeSheetEntriesList';
import EquipmentTimeError from './TimeSplitEntries/EquipmentTimeError';
import EquipmentTimeWarning from './TimeSplitEntries/EquipmentTimeWarning';
import type { OwnProps as TimeSplitEntriesListProps } from './TimeSplitEntries/TimeSplitEntriesList';
import TimeSplitEntriesList from './TimeSplitEntries/TimeSplitEntriesList';
import TotalTimeSheetTimes from './TotalTimeSheetTimes';
import type { TimeSheetAvailableActions } from '../../types';
import CertificationStatusDisplay from '../TimeSheetRow/CertificationStatusDisplay';

type TimeSplitEntryEquipmentSections = {
	title: string;
	options: EquipmentListItemVM[];
};

interface WorkShiftVM {
	accountId: number;
	certificationStatus: Nullable<string>;
	certificationReason: Nullable<string>;
	/** format: ISO_DATETIME */
	startTime: Nullable<string>;
	/** format: ISO_DATETIME */
	endTime: Nullable<string>;
	/** format: ISO_DATETIME */
	outdatedAt: Nullable<string>;
}

export interface OwnProps {
	activeAccounts: { [accountId: number]: true; };
	canManagePerDiem: boolean;
	areFRsReadOnly: boolean;
	canSeeTimeAllocations: boolean;
	timeSheetsAvailableActions: TimeSheetAvailableActions[];
	/** YYYY-MM-DD */
	dueDate: string;
	classificationCodes: FieldWorkClassificationCodeListItemVM[];
	sections: Nullable<TimeSplitEntryEquipmentSections>[];
	subJobs: SubjobVM[];
	isEditable: boolean;
	isVirtual: boolean;
	isWorkOrderPaused: boolean;
	workRequestId: number;
	workOrderReviewStatus: WorkOrderReviewStatus;
	/** Keeps track of which time zone we're using in the form */
	timeZoneInUse: Nullable<string>;
	timeSheetsSyncErrors: FormErrors<EntryValidationErrors, string>;
	temporaryEmployeeAccounts: { [temporaryEmployeeId: number]: true; };
	shouldRoundTimeIntervalsTo1Min: boolean;
	workShifts: WorkShiftVM[];
	getOccupiedSlots: (accountId: number, entries: TimeSheetEntryFormModel[]) => Promise<Nullable<OccupiedSlotsForWorkOrderVM>>;
	isAllowedToEditTimeSheet: (accountId: number) => boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	change: (field: string, value: any) => void;
	isSuperintendent: boolean;
}

type Props = OwnProps & WrappedFieldArrayProps<TimeSheetEditFormModel> & ConnectedProps<typeof connector>;

export enum TimeSheetEntryAction {
	REMOVE = 'REMOVE',
	SAVE = 'SAVE',
	EDITING = 'EDITING',
	RESET = 'RESET'
}

export enum TimeSplitEntryAction {
	REMOVE = 'REMOVE',
	ADD = 'ADD',
	FIELD_CHANGED = 'FIELD_CHANGED'
}

export enum TimeAllocationEntryAction {
	REMOVE = 'REMOVE',
	ADD = 'ADD',
	FIELD_CHANGED = 'FIELD_CHANGED'
}

export interface TimeSheetEntryActionModel {
	action: TimeSheetEntryAction;
	entryIndex: number;
	newEntry?: TimeSheetEntryWithType | TimeSheetAddedEntryWithType;
}

interface WorkShiftType {
	accountId: number;
	certificationStatus: Nullable<string>;
	certificationReason: Nullable<string>;
	/** format: ISO_DATETIME */
	startTime: Nullable<string>;
	/** format: ISO_DATETIME */
	endTime: Nullable<string>;
	/** format: ISO_DATETIME */
	outdatedAt: Nullable<string>;
}

const calculateTotalEquipmentTime = (timeSplitEntries: TimeSplitEntryFormModel[]) => {
	let totalEquipmentTime = 0;
	if (timeSplitEntries?.length > 0) {
		totalEquipmentTime = timeSplitEntries
			.reduce((totalEqTime, entry) => entry.duration + totalEqTime, 0);
	}
	return totalEquipmentTime;
};

const round = (time: number, shouldRoundTimeIntervalsTo1Min: boolean): number =>
	shouldRoundTimeIntervalsTo1Min ? time : TimeUtils.roundTimeDurationToInterval(time, DEFAULT_TIME_DURATION_MINUTE_ROUNDING_INTERVAL);

const TimeSheetEditModalBody: React.FC<Props> = (props) => {
	const {
		activeAccounts,
		fields,
		change,
		classificationCodes,
		canManagePerDiem,
		sections,
		isAllowedToEditTimeSheet,
		workOrderReviewStatus,
		isWorkOrderPaused,
		isEditable,
		isVirtual,
		getOccupiedSlots,
		dueDate,
		areFRsReadOnly,
		timeZoneInUse,
		subJobs,
		workRequestId,
		canSeeTimeAllocations,
		timeSheetsSyncErrors,
		temporaryEmployeeAccounts,
		timeSheetsAvailableActions,
		shouldRoundTimeIntervalsTo1Min,
		isSuperintendent,
		isManagerOrAdmin,
	} = props;
	fields.getAll()?.sort((a, b) => a.userFullName.localeCompare(b.userFullName)) ?? fields;

	const timeZoneOffset = React.useMemo(() => timeZoneInUse ? TimeUtils.getOffset(timeZoneInUse) : 0, [timeZoneInUse]);

	const classificationCodeIdsWithValues = React.useMemo(() => {
		const _classificationCodeIdsWithValues: Record<number, string> = {};
		classificationCodes.forEach((classificationCode) => {
			_classificationCodeIdsWithValues[classificationCode.id] = classificationCode.code;
		});
		return _classificationCodeIdsWithValues;
	}, [classificationCodes]);

	const equipmentIdsWithValues = React.useMemo(() => {
		const _equipmentIdsWithValues: Record<number, string> = {};
		sections.forEach((section) => {
			section?.options.forEach((option) => {
				_equipmentIdsWithValues[option.id] = option.code;
			});
		});
		return _equipmentIdsWithValues;
	}, [sections]);

	const calculateGapsOverlapsAndSortEntries = React.useCallback(async (
		timeSheetEntries: (TimeSheetEntryWithType | TimeSheetGapEntryWithType | TimeSheetOccupiedEntryWithType | TimeSheetAddedEntryWithType)[],
		accountId: number
	) => {
		const formattedEntries = TimeSheetEditFormModel.filterOutGapAndOccupiedEntries(timeSheetEntries).map((_entryWithType) => _entryWithType.entry);

		const occupiedSlots = await getOccupiedSlots(accountId, formattedEntries);
		const overlaps = findOverlapsByVirtualId(formattedEntries, occupiedSlots?.slots);

		const hasOverlap = Object.keys(overlaps).some((_key) => (overlaps[_key].startTime || overlaps[_key].endTime || overlaps[_key].occupied));
		const timelineEntities = getTimelineEntities(formattedEntries, occupiedSlots?.slots);

		const newEntries = TimeSheetEditFormModel.mapTimelineEntitiesToTimeSheetEntriesList(timelineEntities);

		return {
			hasOverlap,
			newEntries,
			overlaps,
		};
	}, [getOccupiedSlots]);

	const changeAndSortEntries = React.useCallback(async (timeSheetField: string, timeSheetIndex: number, actionModel: TimeSheetEntryActionModel) => {
		const timeSheet = fields.get(timeSheetIndex);
		if (!timeSheet) {
			return;
		}
		const timeSheetEntries = [...timeSheet.timeSheetEntries];

		const { action, entryIndex, newEntry: newEntryWithType } = actionModel;

		const previousIndexOfEditingEntry = timeSheet.editingEntryIndex;
		const virtualIdOfEditingEntry = newEntryWithType?.entry.virtualId;

		if (action === TimeSheetEntryAction.REMOVE) {
			timeSheetEntries.splice(entryIndex, 1);
		} else if (action === TimeSheetEntryAction.SAVE) {
			const entryWithType = timeSheetEntries[entryIndex];
			if (entryWithType.type !== TimeSheetEntryFormType.ADDED && entryWithType.type !== TimeSheetEntryFormType.ENTRY) {
				throw new Error('Trying to save entry that is not being edited');
			}
			const newEntry = TimeSheetEditFormModel.addedEntryToListEntry(entryWithType.entry);
			timeSheetEntries.splice(entryIndex, 1, {
				entry: newEntry,
				type: TimeSheetEntryFormType.ENTRY,
			});
		} else if (action === TimeSheetEntryAction.EDITING) {
			if (newEntryWithType) {
				timeSheetEntries.splice(entryIndex, 1, newEntryWithType);
			}
		} else if (action === TimeSheetEntryAction.RESET) {
			if (newEntryWithType) {
				timeSheetEntries.splice(entryIndex, 1, newEntryWithType);
			}
		}

		const { hasOverlap, newEntries, overlaps } = await calculateGapsOverlapsAndSortEntries(timeSheetEntries, timeSheet.accountId);

		if (virtualIdOfEditingEntry && action === TimeSheetEntryAction.EDITING) {
			const indexOfEditingEntry = newEntries.findIndex((entryWithType) => entryWithType.entry.virtualId === virtualIdOfEditingEntry);
			// if there was change in placement of entries due to sorting and gaps/overlaps calculation
			if (indexOfEditingEntry !== -1 && previousIndexOfEditingEntry !== indexOfEditingEntry) {
				change(timeSheetField + '.editingEntryIndex', indexOfEditingEntry);
			}
		}

		change(timeSheetField + '.hasOverlap', hasOverlap);
		change(timeSheetField + '.overlaps', overlaps);
		change(timeSheetField + '.timeSheetEntries', newEntries);

		// set/reset/remove time allocations for that work type
		if (newEntryWithType?.entry.workType ||
			(action === TimeSheetEntryAction.REMOVE && timeSheet.timeSheetEntries[entryIndex].type === TimeSheetEntryFormType.ENTRY)
		) {
			const workType = newEntryWithType?.entry.workType ?? (timeSheet.timeSheetEntries[entryIndex].entry as TimeSheetEntryFormModel).workType;
			const totalDurationOfChangedEntry = TimeSheetEditFormModel.getTotalTimeForWorkType(newEntries, workType);
			const roundedDuration = round(totalDurationOfChangedEntry, shouldRoundTimeIntervalsTo1Min ?? false);
			const newAllocationEntries = TimeAllocationEntryFormModel.resetAllocationEntriesForWorkType(
				[...timeSheet.timeAllocationEntries],
				workType,
				workRequestId,
				roundedDuration
			);
			const previousWorkTypeOfEditingEntry = timeSheet.editingEntryInitialEntry?.type === TimeSheetEntryFormType.ENTRY ?
				timeSheet.editingEntryInitialEntry?.entry.workType : null;
			let renewedAllocationEntries = [] as TimeAllocationEntryFormModel[];
			if (previousWorkTypeOfEditingEntry && previousWorkTypeOfEditingEntry !== workType) {
				const previousWorkTypeDuration = TimeSheetEditFormModel.getTotalTimeForWorkType(newEntries, previousWorkTypeOfEditingEntry);
				const roundedPreviousWorkTypeDuration = round(previousWorkTypeDuration, shouldRoundTimeIntervalsTo1Min ?? false);
				renewedAllocationEntries = TimeAllocationEntryFormModel.resetAllocationEntriesForWorkType(
					newAllocationEntries,
					previousWorkTypeOfEditingEntry,
					workRequestId,
					roundedPreviousWorkTypeDuration
				);
			}
			if (renewedAllocationEntries.length) {
				change(timeSheetField + '.timeAllocationEntries', renewedAllocationEntries);
			} else {
				change(timeSheetField + '.timeAllocationEntries', newAllocationEntries);
			}
			change(timeSheetField + '.timeAllocationEntriesHaveChanged', true);
		}
	}, [calculateGapsOverlapsAndSortEntries, change, fields, shouldRoundTimeIntervalsTo1Min, workRequestId]);

	const changeTimeSplitEntries = React.useCallback(async (
		timeSheetField: string,
		timeSheetIndex: number,
		action: TimeSplitEntryAction,
		entryIndex: Nullable<number>,
		timeDifferenceBetweenJobAndEquipmentTime: number
	) => {
		const timeSheet = fields.get(timeSheetIndex);
		if (!timeSheet) {
			return;
		}
		const timeSplitEntries = [...timeSheet.timeSplitEntries];

		if (action === TimeSplitEntryAction.REMOVE && entryIndex !== null) {
			timeSplitEntries.splice(entryIndex, 1);
		} else if (action === TimeSplitEntryAction.ADD) {
			timeSplitEntries.push(new TimeSplitEntryFormModel(workRequestId, timeDifferenceBetweenJobAndEquipmentTime));
		} else if (action === TimeSplitEntryAction.FIELD_CHANGED) {
			return;
		}

		change(timeSheetField + '.timeSplitEntries', timeSplitEntries);
	}, [change, fields, workRequestId]);

	const changeTimeAllocationEntries = React.useCallback(async (
		timeSheetField: string, timeSheetIndex: number, action: TimeAllocationEntryAction, entryIndex: Nullable<number>
	) => {
		const timeSheet = fields.get(timeSheetIndex);
		if (!timeSheet) {
			return;
		}
		const timeAllocationEntries = [...timeSheet.timeAllocationEntries];

		if (action === TimeAllocationEntryAction.REMOVE && entryIndex !== null) {
			timeAllocationEntries.splice(entryIndex, 1);
		} else if (action === TimeAllocationEntryAction.ADD) {
			timeAllocationEntries.push(new TimeAllocationEntryFormModel(
				workRequestId,
				TimeAllocationEntryFormModel.DEFAULT_WORK_TYPE,
				shouldRoundTimeIntervalsTo1Min
					? TimeAllocationEntryFormModel.ONE_MINUTE_ROUNDING_DEFAULT_DURATION
					: TimeAllocationEntryFormModel.DEFAULT_DURATION
			));
		} else if (action === TimeAllocationEntryAction.FIELD_CHANGED) {
			return;
		}

		change(timeSheetField + '.timeAllocationEntries', timeAllocationEntries);
	}, [change, fields, shouldRoundTimeIntervalsTo1Min, workRequestId]);

	const changeOnlyOverlapsForValidation = React.useCallback(async (timeSheetField: string, timeSheetIndex: number) => {
		const timeSheet = fields.get(timeSheetIndex);
		if (!timeSheet) {
			return;
		}
		const { hasOverlap, overlaps } = await calculateGapsOverlapsAndSortEntries(timeSheet.timeSheetEntries, timeSheet.accountId);

		change(timeSheetField + '.hasOverlap', hasOverlap);
		change(timeSheetField + '.overlaps', overlaps);
	}, [calculateGapsOverlapsAndSortEntries, change, fields]);

	const renderCertStatusList = React.useCallback((workShifts: WorkShiftType[]): React.JSX.Element => {
		return <>{workShifts.map((ws: WorkShiftType) =>
			<CertificationStatusDisplay
				key={`${ws.accountId}:${ws.startTime}-${ws.endTime}`}
				timeZoneOffset={timeZoneOffset}
				workShift={ws}
			/>
		)}</>;
	}, [timeZoneOffset]);

	const renderTimeSheetEntries = React.useCallback((field: string, index: number) => {
		const timeSheet = fields.get(index);
		const isActive = activeAccounts[timeSheet.accountId];
		const isTemporaryEmployee: boolean = temporaryEmployeeAccounts[timeSheet.accountId] ?? false;

		const timeSheetEntries = TimeSheetEditFormModel.mapEntriesWithTypesToEntries(
			TimeSheetEditFormModel.filterOutGapAndOccupiedEntries(timeSheet.timeSheetEntries)
		);
		const totalJobTime = TimeSheetEditFormModel.totalWorkTypeTime(timeSheetEntries, TimeSheetEntryWorkType.JOB);
		const totalShopTime = TimeSheetEditFormModel.totalWorkTypeTime(timeSheetEntries, TimeSheetEntryWorkType.SHOP);
		const totalTravelTime = TimeSheetEditFormModel.totalWorkTypeTime(timeSheetEntries, TimeSheetEntryWorkType.TRAVEL);
		const totalBreakTime = TimeSheetEditFormModel.totalWorkTypeTime(timeSheetEntries, TimeSheetEntryWorkType.BREAK);

		const approvalStatus = timeSheet.approvalStatus;
		const employeeApprovalStatus = timeSheet.employeeApprovalStatus;

		const allowedToEditTimeSheet = isAllowedToEditTimeSheet(timeSheet.accountId);

		const isTimeSheetReadonly = !!approvalStatus?.approvalStatus && TimeSheetUtils.shouldTsBeReadOnly(
			workOrderReviewStatus,
			approvalStatus?.approvalStatus,
			allowedToEditTimeSheet
		);

		const isRejected = (
			approvalStatus.approvalStatus === TimeSheetApprovalStatus.REJECTED
			&& employeeApprovalStatus.signatureStatus !== TimeSheetSignatureStatus.SIGNED
		);

		const indexOfEntryInEdit = timeSheet.editingEntryIndex;

		const changeEntries = (actionModel: TimeSheetEntryActionModel, shouldNotUpdateEntriesHaveChanged: boolean = false) => {
			changeAndSortEntries(field, index, actionModel);
			if (!shouldNotUpdateEntriesHaveChanged) {
				change(field + '.timeSheetEntriesHaveChanged', true);
			}
		};

		const calculateOverlapsForValidation = () => {
			changeOnlyOverlapsForValidation(field, index);
		};

		const setTimeSheetInEditIndexForAllTimeSheets = (timeSheetIndex: Nullable<number>) => {
			fields.forEach((_field) => {
				change(_field + '.timeSheetInEditIndex', timeSheetIndex);
			});
		};

		const setEditingEntryIndexAndValue = (
			_index: Nullable<number>,
			initialEntry: Nullable<TimeSheetEntryWithType | TimeSheetGapEntryWithType>
		) => {
			if (timeSheet.editingEntryIndex !== null
				&& _index === null && initialEntry === null
				|| timeSheet.editingEntryIndex === null && timeSheet.editingEntryInitialEntry === null
				&& _index !== null) {
				change(field + '.editingEntryIndex', _index);
				change(field + '.editingEntryInitialEntry', initialEntry);
				if (_index !== null && timeSheet.timeSheetInEditIndex === null
					|| _index === null && timeSheet.timeSheetInEditIndex !== null) {
					setTimeSheetInEditIndexForAllTimeSheets(_index === null ? null : index);
				}
			}
		};

		const isOtherTimeSheetInEditMode = timeSheet.timeSheetInEditIndex !== null && timeSheet.timeSheetInEditIndex !== index;
		const isThisTimeSheetInEditMode = timeSheet.timeSheetInEditIndex !== null && timeSheet.timeSheetInEditIndex === index;

		const errors = timeSheetsSyncErrors ?
			timeSheetsSyncErrors[index]?.timeSheetEntries as FormErrors<TimeSheetEntryWithType | TimeSheetAddedEntryWithType, string>[]
			: [];

		const modalClassName = `${styles['time-sheet-bulk-edit-modal__bulk-modal-row__user-name']} 
			${isOtherTimeSheetInEditMode ? `${styles['time-sheet-bulk-edit-modal__bulk-modal-row__user-name--opaque']}` : ''} `;

		return (
			<>
				<div
					className={modalClassName}
					key={'username' + index}
				>
					<div className={styles['user-full-name']}> {timeSheet.userFullName}</div>
					{!isTemporaryEmployee && isActive && canManagePerDiem && !isVirtual &&
						<PerDiemForTimeSheet
							change={change}
							field={field}
							isEditable={isEditable}
							perDiemForTS={timeSheet.perDiemInfo}
						/>
					}
				</div >
				{shouldRoundTimeIntervalsTo1Min &&
					<div>
						Certification Status
						<div className={styles['cert-status-block']}>{renderCertStatusList(props.workShifts.filter((_ws) => _ws.accountId === timeSheet.accountId))}</div>
					</div>
				}
				<div className={styles['time-sheet-bulk-edit-modal__timeline']}>
					<TotalTimeSheetTimes
						breakTime={totalBreakTime}
						hasOverlap={timeSheet.hasOverlap}
						isOpaque={isOtherTimeSheetInEditMode}
						jobTime={totalJobTime}
						shopTime={totalShopTime}
						shouldRoundTimeIntervalsTo1Min={shouldRoundTimeIntervalsTo1Min}
						travelTime={totalTravelTime}
					/>
					{isRejected &&
						<RejectionBanner
							isOpaque={isOtherTimeSheetInEditMode}
							rejectedBy={approvalStatus.userName}
							rejectionReason={approvalStatus.note}
						/>
					}
					<FieldArray<TimeSheetEntriesListProps>
						areTimeSheetsReadOnly={isTimeSheetReadonly || isWorkOrderPaused}
						calculateOverlapsForValidation={calculateOverlapsForValidation.bind(this)}
						change={change}
						changeEntries={changeEntries.bind(this)}
						component={TimeSheetEntriesList}
						dueDate={dueDate}
						editingEntryIndex={indexOfEntryInEdit}
						editingEntryInitialEntry={timeSheet.editingEntryInitialEntry}
						employeeApprovalStatus={employeeApprovalStatus}
						entriesSyncErrors={errors}
						isAllowedToEdit={allowedToEditTimeSheet}
						isAnotherTimeSheetInEditMode={isOtherTimeSheetInEditMode}
						isThisTimeSheetInEditMode={isThisTimeSheetInEditMode}
						key={'timeSheetEntries' + index}
						name={field + '.timeSheetEntries'}
						overlaps={timeSheet.overlaps}
						setEditingEntryIndexAndValue={setEditingEntryIndexAndValue.bind(this)}
						shouldRoundTimeIntervalsTo1Min={shouldRoundTimeIntervalsTo1Min}
						timeZoneInUse={timeZoneInUse}
					/>
				</div>
			</>
		);
	}, [fields,
		isAllowedToEditTimeSheet,
		workOrderReviewStatus,
		timeSheetsSyncErrors,
		isWorkOrderPaused,
		isEditable,
		isVirtual,
		canManagePerDiem,
		change,
		dueDate,
		timeZoneInUse,
		changeAndSortEntries,
		changeOnlyOverlapsForValidation,
		temporaryEmployeeAccounts,
		activeAccounts,
		shouldRoundTimeIntervalsTo1Min,
		renderCertStatusList,
		props.workShifts,
	]);

	const subJobCodeById = React.useMemo(() => {
		return subJobs.reduce<Record<number, string>>((_map, _sj) => {
			_map[_sj.id] = _sj.jobCode;
			return _map;
		}, {});
	}, [subJobs]);

	const availableActionsLookup = React.useMemo(() => {
		return timeSheetsAvailableActions.reduce(getToLookupByKey((_tsA) => _tsA.timeSheetId), {});
	}, [timeSheetsAvailableActions]);

	const renderTimeSplits = React.useCallback((field: string, index: number) => {
		const timeSheet = fields.get(index);

		const timeSheetEntries = TimeSheetEditFormModel.mapEntriesWithTypesToEntries(
			TimeSheetEditFormModel.filterOutGapAndOccupiedEntries(timeSheet.timeSheetEntries)
		);
		const totalJobTime = TimeSheetEditFormModel.totalWorkTypeTime(timeSheetEntries, TimeSheetEntryWorkType.JOB);
		const roundedTotalJobTime = round(totalJobTime, shouldRoundTimeIntervalsTo1Min ?? false);
		const totalEquipmentTime = calculateTotalEquipmentTime(timeSheet.timeSplitEntries);

		const jobByWr = (timeSheet.timeAllocationEntries ?? []).reduce(
			(acc, allocation) => {
				if (allocation.workType === TimeSheetEntryWorkType.JOB) {
					const wrId = allocation.allocatedWorkRequestId;
					const dur = allocation.time;
					acc[wrId] = (acc[wrId] ?? 0) + dur;
				}
				return acc;
			},
			{} as Record<number, number>
		);

		const equipmentByWr = (timeSheet.timeSplitEntries ?? []).reduce(
			(acc, entry) => {
				const wrId = entry.allocatedWorkRequestId;
				const dur = entry.duration;
				acc[wrId] = (acc[wrId] ?? 0) + dur;
				return acc;
			},
			{} as Record<number, number>
		);

		const wrIds = Object.keys(equipmentByWr).map(Number);

		const perWrErrors = wrIds
			.map((wrId) => {
				const jobTime = round(jobByWr[wrId] ?? 0, shouldRoundTimeIntervalsTo1Min ?? false);
				const equipmentTime = equipmentByWr[wrId] ?? 0;
				return { wrId, jobTime, equipmentTime };
			})
			.filter(({ jobTime, equipmentTime }) => jobTime !== equipmentTime);

		const hasPerWrOverError = perWrErrors.some(({ jobTime, equipmentTime }) => equipmentTime > jobTime);

		const isOtherTimeSheetInEditMode = timeSheet.timeSheetInEditIndex !== null && timeSheet.timeSheetInEditIndex !== index;
		const isThisTimeSheetInEditMode = timeSheet.timeSheetInEditIndex !== null && timeSheet.timeSheetInEditIndex === index;

		const _isAllowedToEditEquipment = availableActionsLookup[timeSheet.id]?.canEditEquipment ?? false;
		const canSeeClassificationCode = isSuperintendent || isManagerOrAdmin;
		const isAllowedToAddEquipment =
			_isAllowedToEditEquipment &&
			!isVirtual
			&& !isOtherTimeSheetInEditMode
			&& !isThisTimeSheetInEditMode;

		const changeEntries = (action: TimeSplitEntryAction, entryIndex: Nullable<number>) => {
			changeTimeSplitEntries(field, index, action, entryIndex, Math.max(0, roundedTotalJobTime - totalEquipmentTime));
			change(field + '.timeSplitEntriesHaveChanged', true);
		};

		const classModifiers = isOtherTimeSheetInEditMode || isThisTimeSheetInEditMode ? ['opaque', 'spacing-top'] : ['spacing-top'];

		return (

			<div className={bemElement('time-sheet-list', 'equipment-modal', classModifiers)}>
				{perWrErrors.map(({ wrId, jobTime, equipmentTime }) => {
					const isError = equipmentTime > jobTime;
					const label = subJobCodeById[wrId] ?? `Job #${wrId}`;

					if (equipmentTime === jobTime) {
						return null;
					} else if (isError) {
						return (
							<EquipmentTimeError
								equipmentMinutes={equipmentTime}
								jobMinutes={jobTime}
								key={`eq-err-${wrId}`}
								label={label}
							/>
						);
					} else {
						return (
							<EquipmentTimeWarning
								eqMinutes={equipmentTime}
								jobMinutes={jobTime}
								key={`eq-warn-${wrId}`}
								label={label}
							/>
						);

					}
				})}
				<div className={bemElement('time-sheet-list__equipment-modal', 'total-times', { error: hasPerWrOverError })}>
					<div>Total in Equipment: {TimeUtils.minutesToHoursAndMinutes(totalEquipmentTime)}</div>
					<div>Total Job Time: {TimeUtils.minutesToHoursAndMinutes(roundedTotalJobTime)}</div>
				</div>
				<FieldArray<TimeSplitEntriesListProps>
					canEditClassificationCodes={_isAllowedToEditEquipment}
					canSeeClassificationCode={canSeeClassificationCode}
					canSeeTimeAllocations={canSeeTimeAllocations}
					change={change}
					changeEntries={changeEntries.bind(this)}
					classificationCodeIdsWithValues={classificationCodeIdsWithValues}
					classificationCodes={classificationCodes}
					component={TimeSplitEntriesList}
					equipmentIdsWithValues={equipmentIdsWithValues}
					index={index}
					isAllowedToAddEquipment={isAllowedToAddEquipment}
					key={'timeSplitEntries' + index}
					name={field + '.timeSplitEntries'}
					readonly={!_isAllowedToEditEquipment}
					sections={sections}
					shouldRoundTimeIntervalsTo1Min={shouldRoundTimeIntervalsTo1Min}
					subJobs={subJobs}
				/>
			</div>
		);
	}, [
		fields,
		canSeeTimeAllocations,
		change,
		classificationCodeIdsWithValues,
		classificationCodes,
		equipmentIdsWithValues,
		sections,
		subJobs,
		changeTimeSplitEntries,
		subJobCodeById,
		availableActionsLookup,
		isVirtual,
		shouldRoundTimeIntervalsTo1Min,
		isManagerOrAdmin,
		isSuperintendent,
	]);

	const renderTimeAllocations = React.useCallback((field: string, index: number) => {
		const timeSheet = fields.get(index);
		const timeSheetEntries = TimeSheetEditFormModel.mapEntriesWithTypesToEntries(
			TimeSheetEditFormModel.filterOutGapAndOccupiedEntries(timeSheet.timeSheetEntries)
		);
		const allowedToEditTimeSheet = isAllowedToEditTimeSheet(timeSheet.accountId);

		const isOtherTimeSheetInEditMode = timeSheet.timeSheetInEditIndex !== null && timeSheet.timeSheetInEditIndex !== index;
		const isThisTimeSheetInEditMode = timeSheet.timeSheetInEditIndex !== null && timeSheet.timeSheetInEditIndex === index;

		const readonly = !allowedToEditTimeSheet || areFRsReadOnly || isOtherTimeSheetInEditMode || isThisTimeSheetInEditMode;
		const isAllowedToAddTimeAllocations = allowedToEditTimeSheet && !areFRsReadOnly && !isOtherTimeSheetInEditMode && !isThisTimeSheetInEditMode;

		const totalJobTime = TimeSheetEditFormModel.totalWorkTypeTime(timeSheetEntries, TimeSheetEntryWorkType.JOB);
		const roundedTotalJobTime = round(totalJobTime, shouldRoundTimeIntervalsTo1Min ?? false);
		const totalBreakTime = TimeSheetEditFormModel.totalWorkTypeTime(timeSheetEntries, TimeSheetEntryWorkType.BREAK);
		const roundedTotalBreakTime = round(totalBreakTime, shouldRoundTimeIntervalsTo1Min ?? false);
		const totalShopTime = TimeSheetEditFormModel.totalWorkTypeTime(timeSheetEntries, TimeSheetEntryWorkType.SHOP);
		const roundedTotalShopTime = round(totalShopTime, shouldRoundTimeIntervalsTo1Min ?? false);
		const totalTravelTime = TimeSheetEditFormModel.totalWorkTypeTime(timeSheetEntries, TimeSheetEntryWorkType.TRAVEL);
		const roundedTotalTravelTime = round(totalTravelTime, shouldRoundTimeIntervalsTo1Min ?? false);

		const {
			jobAllocationsTime,
			breakAllocationsTime,
			shopAllocationsTime,
			travelAllocationsTime,
		} = TimeAllocationEntryFormModel.mapEntriesToTimesPerWorkType(timeSheet.timeAllocationEntries);

		const showAllocationsTimeWarning = roundedTotalJobTime !== jobAllocationsTime || roundedTotalBreakTime !== breakAllocationsTime
			|| roundedTotalShopTime !== shopAllocationsTime || roundedTotalTravelTime !== travelAllocationsTime;

		const changeEntries = (action: TimeAllocationEntryAction, entryIndex: Nullable<number>) => {
			changeTimeAllocationEntries(field, index, action, entryIndex);
			change(field + '.timeAllocationEntriesHaveChanged', true);
		};

		return (
			<div className={`${styles['time-allocations']} 
			${isOtherTimeSheetInEditMode || isThisTimeSheetInEditMode ? `${styles['time-allocations--opaque']}` : ''} `}
			>
				<div className={styles['time-allocations__title']}>
					Time Allocations
				</div>
				{showAllocationsTimeWarning &&
					<div className={`${styles['time-allocations__allocation-time-warning']}`}>
						<span className="icon-info" />Time sum per work type does not match allocations sum per work type.
					</div>
				}
				<FieldArray<TimeAllocationEntriesListProps>
					change={change}
					changeEntries={changeEntries.bind(this)}
					component={TimeAllocationEntriesList}
					index={index}
					isAllowedToAddTimeAllocations={isAllowedToAddTimeAllocations}
					key={'timeAllocationEntries' + index}
					name={field + '.timeAllocationEntries'}
					readonly={readonly}
					shouldRoundTimeIntervalsTo1Min={shouldRoundTimeIntervalsTo1Min}
					subJobs={subJobs}
				/>
			</div>
		);
	}, [areFRsReadOnly, change, changeTimeAllocationEntries, fields, isAllowedToEditTimeSheet, shouldRoundTimeIntervalsTo1Min, subJobs]);

	return (
		<>
			{fields.map((field, index) => {
				return (
					<div className={styles['time-sheet-bulk-edit-modal__bulk-modal-row']} key={'modalRow' + index}>
						{renderTimeSheetEntries(field, index)}
						{canSeeTimeAllocations && renderTimeAllocations(field, index)}
						{renderTimeSplits(field, index)}
					</div>
				);
			})}
		</>
	);

};

function mapStateToProps(state: RootState) {
	const {
		user: {
			userData,
			companyData,
		},
	} = state;

	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const { role } = userData;
	const { isCompanyAdmin, permissions } = companyData;

	return {
		isManagerOrAdmin: isAllowed(PagePermissions.COMPANY.FIELD_REPORT.MANAGE, permissions, isCompanyAdmin, role),
	};
}

const connector = connect(mapStateToProps, null);

export default connector(TimeSheetEditModalBody);
